import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HomeIcon from "@mui/icons-material/Home";
import {
  API_RESPONSE_CODE,
  BRAND_COLOR,
  BRAND_HOVER,
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_HOMEPAGE,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import {
  getPartialSuccessPath,
  ROUTE_SUBSCRIPTIONS
} from "../../constants/routes";
import type { RootState } from "../../store/store";
import {
  setSelectedPlanForCheckout,
  type SelectedPlanForCheckout
} from "../../store/slices/planSlice";
import AnimatedContainer from "../common/AnimatedContainer";
import { usePollMembershipPayment } from "../hooks/api/usePayment";
import { useRefreshMembershipAfterPayment } from "../../hooks/api/useRefreshMembershipAfterPayment";
import { useGetSubscriptionDetail } from "../../hooks/api/useSubscription";
import type { Subscription } from "../../Graphql/types/subscription";

function selectedPlanFromSubscription(
  subscription: Subscription
): SelectedPlanForCheckout {
  return {
    planId: subscription.planId,
    name: subscription.planName,
    description: null,
    isActive: subscription.status === "ACTIVE",
    createdAt: subscription.createdAt,
    updatedAt: subscription.updatedAt,
    price: subscription.priceAmount ?? 0,
    currency: subscription.priceCurrency ?? "RWF",
    billingCycle: subscription.billingCycleCode,
    billingCycleId: subscription.billingCycleId
  };
}

interface SuccessLocationState {
  paymentId?: string;
  subscriptionId?: string;
  awaitingPayment?: boolean;
}

const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { subscriptionId: subscriptionIdParam } = useParams<{
    subscriptionId: string;
  }>();
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const dispatch = useDispatch();
  const selectedPlanFromRedux = useSelector(
    (state: RootState) => state.plans.selectedPlanForCheckout
  );

  const planData: SelectedPlanForCheckout | null =
    selectedPlanFromRedux ?? null;

  const successState = location.state as SuccessLocationState | null;
  const subscriptionId =
    subscriptionIdParam?.trim() ?? successState?.subscriptionId ?? "";
  const paymentIdFromNav = successState?.paymentId;
  const awaitingPayment =
    Boolean(successState?.awaitingPayment) && Boolean(paymentIdFromNav);

  const { data: paymentPoll, isLoading: paymentPollLoading } =
    usePollMembershipPayment(paymentIdFromNav, awaitingPayment);
  const { refreshMembershipAfterPayment } = useRefreshMembershipAfterPayment();
  const { mutateAsync: fetchSubscriptionDetail, isPending } =
    useGetSubscriptionDetail();
  const membershipRefreshDoneRef = useRef(false);
  const redirectedRef = useRef(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const fetchSubscriptionDetailRef = useRef(fetchSubscriptionDetail);
  const navigateRef = useRef(navigate);
  const dispatchRef = useRef(dispatch);
  const localeRef = useRef({ language, defaultLang });

  fetchSubscriptionDetailRef.current = fetchSubscriptionDetail;
  navigateRef.current = navigate;
  dispatchRef.current = dispatch;
  localeRef.current = { language, defaultLang };

  const iremboPhase:
    "off" | "loading" | "pending" | "failed" | "complete" | "poll_error" =
    (() => {
      if (!awaitingPayment) return "off";
      if (!paymentPoll && paymentPollLoading) return "loading";
      if (!paymentPoll) return "loading";
      const c = paymentPoll.getPayment?.code;
      if (c !== "OK") return "poll_error";
      const s = paymentPoll.getPayment?.payment?.paymentStatus;
      if (s === "COMPLETED") return "complete";
      if (s === "FAILED") return "failed";
      return "pending";
    })();

  const showFullSuccess = iremboPhase === "off" || iremboPhase === "complete";
  const showWaiting = iremboPhase === "loading" || iremboPhase === "pending";
  const showFailure = iremboPhase === "failed" || iremboPhase === "poll_error";

  // Clear Redux when leaving the success page so we don't leave stale data for the next flow
  useEffect(() => {
    return () => {
      if (!redirectedRef.current) {
        dispatch(setSelectedPlanForCheckout(null));
      }
    };
  }, [dispatch]);

  useEffect(() => {
    if (!subscriptionId) {
      const { language: lang, defaultLang: defaultLanguage } =
        localeRef.current;
      redirectedRef.current = true;
      navigateRef.current(
        localizedPath(ROUTE_SUBSCRIPTIONS, lang, defaultLanguage),
        { replace: true }
      );
      return;
    }

    let cancelled = false;
    setAccessChecked(false);

    void (async () => {
      try {
        const subscriptionRes = await fetchSubscriptionDetailRef.current({
          subscriptionId
        });

        if (cancelled) {
          return;
        }

        const subscription =
          subscriptionRes.getSubscription?.code === API_RESPONSE_CODE.OK
            ? subscriptionRes.getSubscription.subscription
            : null;
        const status = subscription?.status;
        const { language: lang, defaultLang: defaultLanguage } =
          localeRef.current;

        if (status === "PENDING") {
          redirectedRef.current = true;
          navigateRef.current(
            localizedPath(
              getPartialSuccessPath(subscriptionId),
              lang,
              defaultLanguage
            ),
            { replace: true }
          );
          return;
        }

        if (status !== "ACTIVE") {
          redirectedRef.current = true;
          navigateRef.current(
            localizedPath(ROUTE_SUBSCRIPTIONS, lang, defaultLanguage),
            { replace: true }
          );
          return;
        }

        if (subscription) {
          dispatchRef.current(
            setSelectedPlanForCheckout(
              selectedPlanFromSubscription(subscription)
            )
          );
        }
      } catch {
        if (!cancelled) {
          const { language: lang, defaultLang: defaultLanguage } =
            localeRef.current;
          redirectedRef.current = true;
          navigateRef.current(
            localizedPath(ROUTE_SUBSCRIPTIONS, lang, defaultLanguage),
            { replace: true }
          );
        }
      } finally {
        if (!cancelled && !redirectedRef.current) {
          setAccessChecked(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [subscriptionId]);

  // Async Irembo payment: refresh membership context once polling reports COMPLETED
  useEffect(() => {
    if (
      !awaitingPayment ||
      iremboPhase !== "complete" ||
      !paymentPoll?.getPayment ||
      membershipRefreshDoneRef.current
    ) {
      return;
    }
    membershipRefreshDoneRef.current = true;
    void refreshMembershipAfterPayment({
      membershipTokenIssued: paymentPoll.getPayment.membershipTokenIssued
    });
  }, [
    awaitingPayment,
    iremboPhase,
    paymentPoll,
    refreshMembershipAfterPayment
  ]);

  const handleGoToSubscriptions = () => {
    dispatch(setSelectedPlanForCheckout(null));
    navigate(localizedPath(ROUTE_SUBSCRIPTIONS, language, defaultLang));
  };

  const handleGoToHome = () => {
    dispatch(setSelectedPlanForCheckout(null));
    navigate(localizedPath(PAGE_ROUTE_HOMEPAGE, language, defaultLang));
  };

  const showAccessLoader =
    Boolean(subscriptionId) && (!accessChecked || isPending);

  if (showAccessLoader) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #6f49c9 50%, #5b36b0 75%, #4c2a85 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <CircularProgress sx={{ color: "white" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #6f49c9 50%, #5b36b0 75%, #4c2a85 100%)",
        position: "relative",
        overflow: "hidden",
        py: 6,
        px: { xs: 2, sm: 3, md: 4 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: "absolute",
          top: "-30%",
          right: "-15%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
          animation: "float 20s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": {
              transform: "translate(0, 0) rotate(0deg)"
            },
            "50%": {
              transform: "translate(-30px, -30px) rotate(180deg)"
            }
          }
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-20%",
          left: "-10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)",
          animation: "float 15s ease-in-out infinite reverse",
          "@keyframes float": {
            "0%, 100%": {
              transform: "translate(0, 0) rotate(0deg)"
            },
            "50%": {
              transform: "translate(30px, 30px) rotate(-180deg)"
            }
          }
        }}
      />

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <AnimatedContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <Paper
            elevation={8}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              p: { xs: 3, sm: 4, md: 6 }
            }}
          >
            {/* Success Icon and Title */}
            <Box
              sx={{
                textAlign: "center",
                mb: 4
              }}
            >
              <AnimatedContainer
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 50,
                    height: 50,
                    borderRadius: "50%",
                    background: showFailure
                      ? "linear-gradient(135deg, #e53935 0%, #c62828 100%)"
                      : showWaiting
                        ? "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)"
                        : "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
                    mb: 3,
                    boxShadow: showFailure
                      ? "0 8px 24px rgba(229, 57, 53, 0.3)"
                      : showWaiting
                        ? "0 8px 24px rgba(255, 152, 0, 0.35)"
                        : "0 8px 24px rgba(76, 175, 80, 0.3)"
                  }}
                >
                  {showWaiting ? (
                    <CircularProgress size={24} sx={{ color: "white" }} />
                  ) : showFailure ? (
                    <Typography sx={{ fontSize: 24, color: "white" }}>
                      !
                    </Typography>
                  ) : (
                    <CheckCircleIcon
                      sx={{
                        fontSize: 30,
                        color: "white"
                      }}
                    />
                  )}
                </Box>
              </AnimatedContainer>

              <Typography
                fontWeight="bold"
                sx={{
                  fontSize: {
                    xs: "1.25rem",
                    sm: "1.60rem",
                    md: "2.65rem",
                    lg: "2.75rem"
                  },
                  lineHeight: 1.3,
                  mb: 2,
                  color: "text.primary",
                  letterSpacing: { xs: "0.01em", md: "0.02em" },
                  textShadow: "0 2px 10px rgba(0,0,0,0.1)"
                }}
              >
                {showWaiting
                  ? t(
                      "subscription-success-waiting-title",
                      "Waiting for payment confirmation"
                    )
                  : showFailure
                    ? t(
                        "subscription-success-failed-title",
                        "Payment could not be confirmed"
                      )
                    : t(
                        "subscription-success-title",
                        "Subscription successful!"
                      )}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                  lineHeight: 1.7,
                  color: "text.secondary",
                  fontWeight: 400,
                  mb: 1
                }}
              >
                {showWaiting
                  ? t(
                      "subscription-success-waiting-subtitle",
                      "Complete the prompt on your phone or finish paying in the checkout tab. This page updates automatically."
                    )
                  : showFailure
                    ? t(
                        "subscription-success-failed-subtitle",
                        "If you were charged, contact support. You can retry from the plans page."
                      )
                    : t(
                        "subscription-success-subtitle",
                        "Your subscription has been activated"
                      )}
              </Typography>
              {showFullSuccess && (
                <Typography variant="body2" color="text.secondary">
                  {t(
                    "subscription-success-thank-you",
                    "Thank you for subscribing. You now have access to all premium features."
                  )}
                </Typography>
              )}
              {planData && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: showFullSuccess ? 1 : 0 }}
                >
                  {`${planData.name} · ${planData.currency} ${planData.price.toLocaleString()} ${planData.billingCycle}`}
                </Typography>
              )}
            </Box>

            {showFullSuccess && (
              <>
                <Divider sx={{ my: 4 }} />
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ mb: 2, color: "text.primary" }}
                  >
                    {t("subscription-success-whats-next", "What's next?")}
                  </Typography>
                  <List>
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: "success.main" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={t(
                          "subscription-success-next-access-primary",
                          "Access your subscription"
                        )}
                        secondary={t(
                          "subscription-success-next-access-secondary",
                          "View and manage your subscription from the subscriptions page"
                        )}
                        primaryTypographyProps={{
                          fontWeight: "medium"
                        }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: "success.main" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={t(
                          "subscription-success-next-explore-primary",
                          "Explore premium features"
                        )}
                        secondary={t(
                          "subscription-success-next-explore-secondary",
                          "Start using all the premium features included in your plan"
                        )}
                        primaryTypographyProps={{
                          fontWeight: "medium"
                        }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: "success.main" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={t(
                          "subscription-success-next-support-primary",
                          "Get support"
                        )}
                        secondary={t(
                          "subscription-success-next-support-secondary",
                          "Contact our support team if you need any assistance"
                        )}
                        primaryTypographyProps={{
                          fontWeight: "medium"
                        }}
                      />
                    </ListItem>
                  </List>
                </Box>
              </>
            )}

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                mt: 4
              }}
            >
              <Button
                variant="contained"
                fullWidth
                onClick={handleGoToSubscriptions}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  backgroundColor: BRAND_COLOR,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  "&:hover": {
                    backgroundColor: BRAND_HOVER,
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 16px rgba(111, 73, 201, 0.3)"
                  },
                  transition: "all 0.3s ease"
                }}
              >
                {t(
                  "subscription-success-btn-subscriptions",
                  "View my subscriptions"
                )}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleGoToHome}
                startIcon={<HomeIcon />}
                sx={{
                  borderColor: BRAND_COLOR,
                  color: BRAND_COLOR,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: "bold",
                  textTransform: "none",
                  fontSize: "1rem",
                  "&:hover": {
                    borderColor: BRAND_HOVER,
                    backgroundColor: "rgba(111, 73, 201, 0.04)",
                    transform: "translateY(-2px)"
                  },
                  transition: "all 0.3s ease"
                }}
              >
                {t("subscription-success-btn-home", "Back to home")}
              </Button>
            </Box>
          </Paper>
        </AnimatedContainer>
      </Container>
    </Box>
  );
};

export default SubscriptionSuccess;
